<template>
  <button
    :aria-label="$t('quote.select-timeslot', {
      timeslot: getAppointmentTime(timeslot.start_at),
    })"
    type="button"
    :data-selected-timeslot="isTimeslotSelected(timeslot)"
    class="w-auto lg:w-full"
    @click.stop=" $store.dispatch(
      'quote/updateTimeslotAndFetchQuote',
      timeslot,
    )"
  >
    <div
      v-if="isLargeScreenLayout"
      class="flex items-center outline rounded-lg py-4 px-4 mt-4 hover:opacity-70 w-full"
      :class="{
        'outline-bubblegum-500 outline-2': isTimeslotSelected(timeslot),
        'outline-neutral-900 outline-1': !isTimeslotSelected(timeslot),
        'justify-center min-h-14': hidePricing,
        'justify-between': !hidePricing,
      }"
    >
      <fa
        v-if="isQuoteLoading && isTimeslotSelected(timeslot)"
        :icon="['fas', 'circle-notch']"
        spin
        class="text-base text-bubblegum-700 py-1"
      />
      <template v-else>
        <span>
          <span class="text-neutral-900">
            {{ buttonDate(timeslot.start_at, getInputs.timezone, true) }} {{ $t('common.at') }}
          </span>
          <span
            class="font-bold"
            :class="{ 'text-bubblegum-700': isTimeslotSelected(timeslot) }"
          >
            {{ getTime(timeslot, getInputs.timezone) }}
          </span>
        </span>
        <vetster-discount
          v-if="!hidePricing"
          label-class="font-bold text-neutral-900"
        >
          <span class="font-bold text-neutral-900">
            {{ $n(formatMoneyRounded(amount), {
              style: 'currency',
              currency: getConversionCurrency || timeslot.rate.currency,
              maximumFractionDigits: 0,
              minimumFractionDigits: 0,
            }) }}
          </span>
        </vetster-discount>
      </template>
    </div>
    <div
      v-else
      class="flex flex-col justify-center items-center border rounded-lg space-y-1 py-1 w-28 h-16"
      :class="{
        'text-bubblegum-700 border-bubblegum-500 border-2': isTimeslotSelected(timeslot),
        'border-neutral-900': !isTimeslotSelected(timeslot),
      }"
    >
      <fa
        v-if="isQuoteLoading && isTimeslotSelected(timeslot)"
        :icon="['fas', 'circle-notch']"
        class="text-base text-bubblegum-700"
        spin
      />
      <div
        v-else
        class="flex flex-col items-center justify-center"
      >
        <span class="block font-bold text-sm">
          <template>
            {{ getTime(timeslot, getInputs.timezone) }}
          </template>
        </span>
        <vetster-discount
          v-if="!hidePricing"
          class="text-sm text-neutral-500"
          slot-class="text-sm mx-auto text-neutral-500"
        >
          {{ $n(formatMoneyRounded(amount), {
            style: 'currency',
            currency: getConversionCurrency || timeslot.rate.currency,
            maximumFractionDigits: 0,
            minimumFractionDigits: 0,
          }) }}
        </vetster-discount>
      </div>
    </div>
  </button>
</template>

<script>
import { DateTime } from 'luxon';
import { getCurrentInstance } from 'vue';
import { mapGetters } from 'vuex';

import VetsterDiscount from '@/components/VetsterDiscount';
import useAppointmentDatetimeFormat from '@/composables/use-appointment-datetime-format';
import useCheckoutHelpers from '@/composables/use-checkout-helpers';
import useTimeslotHelpers from '@/composables/use-timeslot-helpers';
import { formatMoneyRounded } from '@/helpers/format';
import HasConversionRate from '@/mixins/HasConversionRate';
import SelectsTimeslot from '@/mixins/SelectsTimeslot';

export default {
  name: 'VetsterQuotePanelTimeslotButton',

  components: { VetsterDiscount },

  mixins: [
    HasConversionRate,
    SelectsTimeslot,
  ],

  props: {
    isLargeScreenLayout: {
      type: Boolean,
      default: false,
    },
    timeslot: {
      type: [Object, Array],
      default: () => ({}),
    },
  },

  setup() {
    const root = getCurrentInstance().proxy.$root;
    const { getAppointmentTime } = useAppointmentDatetimeFormat();
    const { isSubscription } = useCheckoutHelpers(root);
    const { buttonDate } = useTimeslotHelpers();

    return {
      buttonDate,
      getAppointmentTime,
      isSubscription,
    };
  },

  computed: {
    ...mapGetters('quote', [
      'isQuoteLoading',
      'isStandardPricing',
    ]),
    ...mapGetters('search', ['hidePriceForSearchPet']),
    amount() {
      return this.convertCurrency(this.timeslot.rate.amount);
    },
    hidePricing() {
      return this.hidePriceForSearchPet || this.isSubscription || this.isStandardPricing;
    },
  },

  methods: {
    formatMoneyRounded,

    getTime(timeslot, timezone) {
      const time = DateTime.fromISO(timeslot.start_at, {
        zone: timezone,
      });

      if (time.hour === 12 && time.minute === 0) {
        return this.$t('quote.noon');
      }

      if (time.hour === 0 && time.minute === 0) {
        return this.$t('quote.midnight');
      }

      return time.toFormat('t');
    },
  },
};
</script>
